import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { AlertTriangle, Loader2, RefreshCw, Sparkles, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { API } from "../constants";

const REPORT_COPY = {
  ko: {
    title: "AI 콘텐츠 전략 리포트",
    description: "선택한 그룹 기준으로 스필오버 신호를 바탕으로 다음 배포 우선순위를 정리합니다.",
    autoBadge: "자동 생성",
    refresh: "다시 생성",
    refreshing: "생성 중...",
    cached: "캐시 결과",
    live: "최신 생성",
    eventCount: "이벤트",
    generatedAt: "생성 시각",
    loadingTitle: "전략 리포트를 생성하고 있습니다",
    loadingDesc: "선택한 그룹의 스필오버 데이터를 정리해 실행 가능한 인사이트로 변환 중입니다.",
    errorTitle: "리포트를 생성하지 못했습니다",
    errorDesc: "잠시 후 다시 시도해 주세요.",
    empty: "그룹을 선택하면 리포트가 자동으로 생성됩니다.",
    errorToast: "콘텐츠 전략 리포트 생성에 실패했습니다",
  },
  en: {
    title: "AI Content Strategy Report",
    description: "Turns spillover signals into concrete distribution priorities for the selected group.",
    autoBadge: "Auto-generated",
    refresh: "Regenerate",
    refreshing: "Generating...",
    cached: "Cached",
    live: "Fresh",
    eventCount: "Events",
    generatedAt: "Generated",
    loadingTitle: "Generating strategy report",
    loadingDesc: "Turning the selected group's spillover data into an actionable plan.",
    errorTitle: "Could not generate the report",
    errorDesc: "Please try again.",
    empty: "Select a group to generate the report automatically.",
    errorToast: "Failed to generate content strategy report",
  },
  zh: {
    title: "AI 内容策略报告",
    description: "基于溢出信号为所选分组整理下一步分发优先级。",
    autoBadge: "自动生成",
    refresh: "重新生成",
    refreshing: "生成中...",
    cached: "缓存",
    live: "最新",
    eventCount: "事件",
    generatedAt: "生成时间",
    loadingTitle: "正在生成策略报告",
    loadingDesc: "正在将所选分组的溢出数据转化为可执行方案。",
    errorTitle: "报告生成失败",
    errorDesc: "请稍后重试。",
    empty: "选择分组后报告将自动生成。",
    errorToast: "内容策略报告生成失败",
  },
};

const parseMarkdownBlocks = (markdown) => {
  const blocks = [];
  let listItems = [];
  let ordered = false;

  const flushList = () => {
    if (!listItems.length) return;
    blocks.push({ type: ordered ? "ol" : "ul", items: [...listItems] });
    listItems = [];
  };

  markdown.split("\n").forEach((line) => {
    const trimmed = line.trim();
    if (!trimmed) {
      flushList();
      blocks.push({ type: "space" });
      return;
    }

    const orderedMatch = trimmed.match(/^\d+\.\s+(.*)$/);
    if (orderedMatch) {
      if (listItems.length && !ordered) flushList();
      ordered = true;
      listItems.push(orderedMatch[1]);
      return;
    }

    if (trimmed.startsWith("- ") || trimmed.startsWith("* ")) {
      if (listItems.length && ordered) flushList();
      ordered = false;
      listItems.push(trimmed.slice(2));
      return;
    }

    flushList();
    ordered = false;

    if (trimmed.startsWith("### ")) { blocks.push({ type: "h3", text: trimmed.slice(4) }); return; }
    if (trimmed.startsWith("## ")) { blocks.push({ type: "h2", text: trimmed.slice(3) }); return; }
    if (trimmed.startsWith("# ")) { blocks.push({ type: "h1", text: trimmed.slice(2) }); return; }

    blocks.push({ type: "p", text: trimmed });
  });

  flushList();
  return blocks;
};

const renderInline = (text) => {
  if (!text) return text;
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => part.startsWith("**") && part.endsWith("**") ? <strong key={i} className="font-semibold text-foreground">{part.slice(2, -2)}</strong> : part);
};

const MarkdownReport = ({ markdown }) => {
  const blocks = useMemo(() => parseMarkdownBlocks(markdown || ""), [markdown]);

  return (
    <div className="space-y-3" data-testid="content-strategy-report-body">
      {blocks.map((block, index) => {
        if (block.type === "space") return <div key={`space-${index}`} className="h-1" />;
        if (block.type === "h1") return <h2 key={`h1-${index}`} className="text-2xl font-semibold tracking-tight text-foreground">{renderInline(block.text)}</h2>;
        if (block.type === "h2") return <h3 key={`h2-${index}`} className="pt-2 text-lg font-semibold text-foreground">{renderInline(block.text)}</h3>;
        if (block.type === "h3") return <h4 key={`h3-${index}`} className="text-sm font-semibold uppercase tracking-[0.18em] text-muted-foreground">{renderInline(block.text)}</h4>;
        if (block.type === "ol") return <ol key={`ol-${index}`} className="space-y-2 pl-5 text-sm leading-7 text-muted-foreground list-decimal">{block.items.map((item, i) => <li key={`ol-${i}`}>{renderInline(item)}</li>)}</ol>;
        if (block.type === "ul") return <ul key={`ul-${index}`} className="space-y-2 pl-5 text-sm leading-7 text-muted-foreground list-disc">{block.items.map((item, i) => <li key={`ul-${i}`}>{renderInline(item)}</li>)}</ul>;
        return <p key={`p-${index}`} className="text-sm leading-7 text-muted-foreground">{renderInline(block.text)}</p>;
      })}
    </div>
  );
};

export const ContentStrategyReport = ({ lang = "ko", headers, selectedGroup, days }) => {
  const copy = REPORT_COPY[lang] || REPORT_COPY.ko;
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchReport = useCallback(async (forceRefresh = false) => {
    if (!selectedGroup) return;
    if (!forceRefresh) setReport(null);
    setLoading(true);
    setError("");
    try {
      const response = await axios.post(`${API}/spillover/content-strategy-report`, null, {
        headers,
        params: { group_id: selectedGroup, days, language: lang, force_refresh: forceRefresh },
      });
      setReport(response.data);
    } catch (requestError) {
      const message = requestError.response?.data?.detail || copy.errorDesc;
      setError(message);
      if (forceRefresh) toast.error(copy.errorToast);
    } finally {
      setLoading(false);
    }
  }, [copy.errorDesc, copy.errorToast, days, headers, lang, selectedGroup]);

  useEffect(() => {
    if (!selectedGroup) { setReport(null); setError(""); return; }
    fetchReport(false);
  }, [fetchReport, selectedGroup]);

  return (
    <Card data-testid="content-strategy-report-card" className="border-amber-500/20 bg-gradient-to-b from-amber-500/6 via-background to-background">
      <CardHeader className="pb-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-1.5">
            <div className="flex flex-wrap items-center gap-2">
              <Badge data-testid="content-strategy-report-auto-badge" variant="outline" className="border-amber-500/30 bg-amber-500/10 text-amber-600">
                <Sparkles className="mr-1 h-3.5 w-3.5" />
                {copy.autoBadge}
              </Badge>
              {report ? (
                <Badge data-testid="content-strategy-report-cache-badge" variant="outline" className="border-border/60 bg-background/70 text-foreground">
                  {report.cached ? copy.cached : copy.live}
                </Badge>
              ) : null}
            </div>
            <CardTitle className="text-sm font-semibold">{copy.title}</CardTitle>
            <CardDescription className="max-w-2xl">{copy.description}</CardDescription>
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => fetchReport(true)}
            disabled={!selectedGroup || loading}
            data-testid="content-strategy-report-refresh-button"
          >
            {loading ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="mr-1 h-3.5 w-3.5" />}
            {loading ? copy.refreshing : copy.refresh}
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {!selectedGroup ? (
          <div className="rounded-3xl border border-dashed border-border/60 bg-background/60 p-8 text-center text-sm text-muted-foreground" data-testid="content-strategy-report-empty-state">
            {copy.empty}
          </div>
        ) : null}

        {selectedGroup && loading && !report ? (
          <div className="space-y-4" data-testid="content-strategy-report-loading-state">
            <div className="rounded-3xl border border-border/60 bg-background/70 p-5">
              <div className="flex items-start gap-3">
                <div className="rounded-full border border-amber-500/20 bg-amber-500/10 p-2">
                  <Loader2 className="h-4 w-4 animate-spin text-amber-600" />
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-medium text-foreground">{copy.loadingTitle}</p>
                  <p className="text-sm text-muted-foreground">{copy.loadingDesc}</p>
                </div>
              </div>
            </div>
            <Skeleton className="h-80 w-full rounded-3xl" />
          </div>
        ) : null}

        {selectedGroup && error && !report ? (
          <div className="rounded-3xl border border-rose-500/20 bg-rose-500/5 p-5" data-testid="content-strategy-report-error-state">
            <div className="flex items-start gap-3">
              <div className="rounded-full border border-rose-500/20 bg-rose-500/10 p-2">
                <AlertTriangle className="h-4 w-4 text-rose-500" />
              </div>
              <div className="space-y-2">
                <p className="text-sm font-medium text-foreground">{copy.errorTitle}</p>
                <p className="text-sm text-muted-foreground">{error || copy.errorDesc}</p>
                <Button type="button" size="sm" variant="outline" onClick={() => fetchReport(true)} data-testid="content-strategy-report-retry-button">
                  <RefreshCw className="mr-1 h-3.5 w-3.5" />
                  {copy.refresh}
                </Button>
              </div>
            </div>
          </div>
        ) : null}

        {report ? (
          <>
            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground" data-testid="content-strategy-report-meta-row">
              <TrendingUp className="h-3.5 w-3.5" />
              <span>{copy.eventCount} {report.analysis_snapshot?.total_spillover_events || 0}</span>
              <span>·</span>
              <span>{days}{lang === "ko" ? "일" : lang === "zh" ? "天" : "d"}</span>
              <span>·</span>
              <span data-testid="content-strategy-report-generated-at">{copy.generatedAt} {new Date(report.generated_at).toLocaleString(lang === "ko" ? "ko-KR" : lang === "zh" ? "zh-CN" : "en-US")}</span>
            </div>
            <div className="rounded-[28px] border border-border/60 bg-background/80 p-5 md:p-6" data-testid="content-strategy-report-markdown-card">
              <MarkdownReport markdown={report.report_markdown} />
            </div>
          </>
        ) : null}
      </CardContent>
    </Card>
  );
};
